import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Loader2, Mail } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

import { StatusBanner } from "@/components/status-banner";
import { requestPasswordReset } from "@/lib/apiSdk";
import { Button } from "@scribe/ui/button";
import { Card, CardContent } from "@scribe/ui/card";
import { Input } from "@scribe/ui/input";
import { Label } from "@scribe/ui/label";

export const Route = createFileRoute("/forgot-password")({
  ssr: false,
  component: ForgotPasswordPage,
});

const schema = z.object({
  email: z.email("Enter a valid email address"),
});
type Values = z.infer<typeof schema>;

function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: "" },
  });

  const onSubmit = form.handleSubmit(async ({ email }) => {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      await requestPasswordReset(email.trim());
      // The endpoint is deliberately uniform for known and unknown addresses, so
      // this screen must be too — anything else turns it into an account oracle.
      setSent(true);
      void navigate({
        to: "/reset-password",
        search: { email: email.trim() },
      });
    } catch (err: unknown) {
      const status =
        typeof err === "object" && err && "status" in err
          ? Number((err as { status?: unknown }).status)
          : undefined;
      setError(
        status === 429
          ? "Too many requests. Please wait a moment and try again."
          : "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  });

  return (
    <div className="flex h-screen w-full">
      <div className="bg-background lg:bg-muted flex w-full flex-col justify-center p-0 lg:p-8">
        <Card className="bg-background lg:bg-card mx-auto flex min-h-screen w-full max-w-none flex-col justify-center overflow-hidden rounded-none border-none py-0 shadow-none lg:min-h-0 lg:max-w-sm lg:rounded-xl lg:border lg:py-6 lg:shadow-sm">
          <CardContent className="p-8">
            <form className="flex flex-col gap-6" onSubmit={onSubmit}>
              <div className="bg-primary/10 mx-auto flex size-16 items-center justify-center rounded-full">
                <Mail className="text-primary size-8" />
              </div>

              <div className="flex flex-col gap-2 text-center">
                <h1 className="text-2xl font-semibold">Reset your password</h1>
                <p className="text-muted-foreground text-sm">
                  Enter your email and we'll send you a code to set a new
                  password.
                </p>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="email" className="text-sm font-medium">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  className="h-11"
                  autoComplete="email"
                  aria-invalid={!!form.formState.errors.email}
                  {...form.register("email")}
                />
                {form.formState.errors.email && (
                  <p className="text-destructive text-xs">
                    {form.formState.errors.email.message}
                  </p>
                )}
              </div>

              <StatusBanner error={error} successMsg={null} />

              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={loading || sent}
              >
                {loading && <Loader2 className="mr-2 size-4 animate-spin" />}
                {loading ? "Sending..." : "Send reset code"}
              </Button>

              <div className="text-center">
                <Link
                  to="/signin"
                  className="text-muted-foreground hover:text-foreground inline-flex items-center text-sm"
                >
                  <ArrowLeft className="mr-1 size-3" />
                  Back to sign in
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
